import { upgradeSkillWithAoeRangeCard } from "@/character/skills/aoe-range";
import { upgradeSkillWithBuffDurationCard } from "@/character/skills/buff-duration";
import { upgradeSkillWithApCard } from "@/character/skills/buff-ap";
import { upgradeSkillWithCooldownCard } from "@/character/skills/cooldown-upgrade";
import {
  canAddSkillToLibrary,
  consumeSkillCreateCard,
  skillLibrarySlotsUnlocked,
} from "@/character/skills/skill-library-limit";
import type { SkillDefinition } from "@/character/skills/types";
import type { HomeEconomy } from "@/home/economy";
import { consumePetSkillCreateCard, petSkillLibrarySlotsUnlocked } from "@/pet/skills/pet-skill-library";
import {
  upgradePetSkillAoeRangeCard,
  upgradePetSkillBuffDurationCard,
  upgradePetSkillCooldownCard,
  upgradePetSkillWithApCard,
} from "@/pet/skills/upgrades";

export type SkillCardPool = "player" | "pet";

export type SkillPoolConfig = {
  label: string;
  librarySlotsUnlocked: (economy: HomeEconomy) => number;
  createCards: (economy: HomeEconomy) => number;
  cooldownCards: (economy: HomeEconomy) => number;
  apCards: (economy: HomeEconomy) => number;
  buffDurationCards: (economy: HomeEconomy) => number;
  aoeRangeCards: (economy: HomeEconomy) => number;
  slotsUnlocked: (economy: HomeEconomy) => number;
  canAddToLibrary: (libraryLen: number, economy: HomeEconomy) => { ok: boolean; reason?: string };
  consumeCreateCard: (economy: HomeEconomy) => HomeEconomy | null;
  upgradeCooldown: (economy: HomeEconomy, skill: SkillDefinition) => {
    ok: true;
    economy: HomeEconomy;
    skill: SkillDefinition;
  } | { ok: false; reason: string };
  upgradeAp: (economy: HomeEconomy, skill: SkillDefinition) => {
    ok: true;
    economy: HomeEconomy;
    skill: SkillDefinition;
  } | { ok: false; reason: string };
  upgradeDuration: (economy: HomeEconomy, skill: SkillDefinition) => {
    ok: true;
    economy: HomeEconomy;
    skill: SkillDefinition;
  } | { ok: false; reason: string };
  upgradeAoe: (economy: HomeEconomy, skill: SkillDefinition) => {
    ok: true;
    economy: HomeEconomy;
    skill: SkillDefinition;
  } | { ok: false; reason: string };
};

const playerPool: SkillPoolConfig = {
  label: "สกิล",
  librarySlotsUnlocked: skillLibrarySlotsUnlocked,
  createCards: (e) => e.skillCreateCards,
  cooldownCards: (e) => e.skillCooldownCards,
  apCards: (e) => e.skillApCards,
  buffDurationCards: (e) => e.skillBuffDurationCards,
  aoeRangeCards: (e) => e.skillAoeRangeCards,
  slotsUnlocked: (e) => e.skillSlotsUnlocked,
  canAddToLibrary: canAddSkillToLibrary,
  consumeCreateCard: consumeSkillCreateCard,
  upgradeCooldown: upgradeSkillWithCooldownCard,
  upgradeAp: upgradeSkillWithApCard,
  upgradeDuration: upgradeSkillWithBuffDurationCard,
  upgradeAoe: upgradeSkillWithAoeRangeCard,
};

const petPool: SkillPoolConfig = {
  label: "สกิลสัตว์เลี้ยง",
  librarySlotsUnlocked: petSkillLibrarySlotsUnlocked,
  createCards: (e) => e.petSkillCreateCards,
  cooldownCards: (e) => e.petSkillCooldownCards,
  apCards: (e) => e.petSkillApCards,
  buffDurationCards: (e) => e.petSkillBuffDurationCards,
  aoeRangeCards: (e) => e.petSkillAoeRangeCards,
  slotsUnlocked: (e) => e.petSkillSlotsUnlocked,
  canAddToLibrary: (len, economy) => {
    if (len >= petSkillLibrarySlotsUnlocked(economy)) {
      return {
        ok: false,
        reason: `คลังสกิลสัตว์เต็ม (${len}/${petSkillLibrarySlotsUnlocked(economy)})`,
      };
    }
    if (economy.petSkillCreateCards < 1) {
      return { ok: false, reason: "ไม่มีบัตรสร้างสกิลสัตว์ · ซื้อที่ร้าน Cash" };
    }
    return { ok: true };
  },
  consumeCreateCard: consumePetSkillCreateCard,
  upgradeCooldown: upgradePetSkillCooldownCard,
  upgradeAp: upgradePetSkillWithApCard,
  upgradeDuration: upgradePetSkillBuffDurationCard,
  upgradeAoe: upgradePetSkillAoeRangeCard,
};

export function getSkillPoolConfig(pool: SkillCardPool): SkillPoolConfig {
  return pool === "pet" ? petPool : playerPool;
}
